import {
  DefaultSelectComponentModel,
  SelectComponent
} from 'components/CampaignLimitation/SelectComponent';
import { Page } from 'components/Page/Page';
import { useCallback, useMemo, useState } from 'react';
import i18n from 'i18n';
import { useFormikContext } from 'formik';
import {
  ProductGroupType,
  ProductGroupFormValue,
  AUTO_UPDATE_VALUE,
  ALL_SELECT_VALUE
} from 'core/limitation/ProductGroup';
import {
  DomainCustomInputComponentModel,
  MachineCustomInputComponentModel,
  SpaceCustomInputComponentModel,
  StoreCustomInputComponentModel
} from 'components/CampaignLimitation';
import { ItemType } from 'components/CampaignLimitation/SelectItemComponent';
import styles from './productGroupForm.module.scss';
import LimitationSet from 'components/CampaignLimitation/LimitationSet/LimitationSet';
import { capitalize, defaultTo, get, remove, uniqBy } from 'lodash';
import Tips from 'components/common/Tips/Tips';
import { readFromClipboard, writeToClipboard } from 'helper/ClipboardHelper';
import {
  buildValueLabelMap,
  textToSelectOptions
} from 'utils/SelectOptionsUtils';
import classNames from 'classnames';
import { DeliveryUnit } from 'core/channelGroup/ChannelGroup';
import { getDeliveryUnitI18nKeys, getOutdoorDeliveryUnit } from './utils';

const cx = classNames.bind(styles);

export const SelectGroupSection: React.FC<{
  options: SelectOptions[];
  outdoorOptions: SelectOptions[];
}> = ({ options, outdoorOptions }) => {
  const {
    values,
    errors,
    touched: formikTouched,
    submitCount,
    setFieldValue
  } = useFormikContext<ProductGroupFormValue>();
  const [showInventory, setShowInventory] = useState<boolean>(true);
  const error = get(errors, 'groupValues');
  const touched =
    defaultTo(submitCount, 0) > 0 || get(formikTouched, 'groupValues');
  const hasError = error !== undefined && touched;
  const groupType = values.groupType;
  const isOutdoor =
    groupType === ProductGroupType.ADSPACE && values.adFormat === 'Outdoor';
  const deliveryUnit = isOutdoor
    ? getOutdoorDeliveryUnit(values.adFormat, values.channelGroupId)
    : undefined;
  const isStoreUnit = deliveryUnit === DeliveryUnit.STORE;
  const isDeviceUnit = deliveryUnit === DeliveryUnit.DEVICE;
  const customInputModelGetter = useCallback(() => {
    return groupType === ProductGroupType.ADSPACE
      ? outdoorOptions.length > 0
        ? isStoreUnit
          ? new StoreCustomInputComponentModel(
              outdoorOptions.flatMap((option) => option.options || [option])
            )
          : new MachineCustomInputComponentModel(
              outdoorOptions.flatMap((option) => option.options || [option])
            )
        : new SpaceCustomInputComponentModel(
            i18n.t('limitation.addSpace'),
            i18n.t('limitation.addSpaceTip'),
            i18n.t('limitation.placeholders.addSpace'),
            'row-reverse'
          )
      : new DomainCustomInputComponentModel(
          i18n.t('productGroupForm.addDomain'),
          i18n.t('limitation.addDomainTip'),
          i18n.t('productGroupForm.placeholders.addDomain'),
          'row-reverse'
        );
  }, [isStoreUnit, groupType, outdoorOptions]);

  const onGroupsChange = useCallback(
    (newGroups: SelectOptions[]) => {
      const allCustoms = remove(
        newGroups,
        (group) => group.extra === 'isCustom'
      );
      setFieldValue('customGroupValues', allCustoms);

      if (newGroups.length === 0) {
        setFieldValue('groupValues', []);
        return;
      }
      const newItem = newGroups[newGroups.length - 1];
      if (newItem.value === AUTO_UPDATE_VALUE) {
        setFieldValue('groupValues', [newItem]);
      } else if (newItem.value === ALL_SELECT_VALUE) {
        const allOptions = options
          .slice(2)
          .flatMap((opt) => (opt.options ? opt.options : [opt]));
        setFieldValue('groupValues', allOptions);
      } else {
        remove(newGroups, (group) => group.value === AUTO_UPDATE_VALUE);
        setFieldValue('groupValues', newGroups);
      }
    },
    [options, setFieldValue]
  );

  const outdoorLabelMap = useMemo(
    () => buildValueLabelMap(outdoorOptions),
    [outdoorOptions]
  );

  const groupValues = useMemo(() => {
    const combined = uniqBy(
      [...values.groupValues, ...values.customGroupValues],
      'value'
    );
    return combined.map((item) => ({
      ...item,
      label: outdoorLabelMap.get(item.value) ?? item.label
    }));
  }, [values.groupValues, values.customGroupValues, outdoorLabelMap]);

  const selectComponentModel = useMemo(() => {
    return new DefaultSelectComponentModel(
      'groupValues',
      false,
      {
        type: ItemType.auto,
        categorySelectable: !isOutdoor,
        readonly: false
      },
      i18n.t('productGroup.placeholders.search'),
      (newGroups) => onGroupsChange(newGroups),
      options,
      groupValues,
      customInputModelGetter
    );
  }, [options, groupValues, customInputModelGetter, onGroupsChange, isOutdoor]);

  const onClearGroups = useCallback(() => {
    setFieldValue('groupValues', []);
    setFieldValue('customGroupValues', []);
  }, [setFieldValue]);

  const handleCopyToClipboard = useCallback(() => {
    const groupValues = defaultTo(values.groupValues, []);
    remove(groupValues, (option) =>
      [ALL_SELECT_VALUE, AUTO_UPDATE_VALUE].includes(option.value.toString())
    );
    const groupValuesText = groupValues.reduce(
      (acc, value) =>
        acc.concat(value.value.toString(), ',', value.label, '\n'),
      ''
    );

    const customValues = defaultTo(values.customGroupValues, []);
    const customValuesText = customValues.reduce(
      (acc, value) =>
        acc.concat(value.value.toString(), ',', value.label, '\n'),
      ''
    );

    writeToClipboard(`${groupValuesText}${customValuesText}`);
  }, [values.groupValues, values.customGroupValues]);

  const handlePasteFromClipboard = useCallback(
    async (e) => {
      e.preventDefault();
      const groupValues = defaultTo(values.groupValues, []);
      const customGroupValues = defaultTo(values.customGroupValues, []);
      const pasteText = await readFromClipboard();
      const groupValuesToAdd = textToSelectOptions(pasteText);
      const customValuesToAdd = remove(
        groupValuesToAdd,
        (value) =>
          options.find((option) => option.value === value.value) === undefined
      );
      customValuesToAdd.forEach((option) => ({ ...option, extra: 'isCustom' }));

      setFieldValue(
        'groupValues',
        uniqBy([...groupValues, ...groupValuesToAdd], 'value')
      );
      setFieldValue(
        'customGroupValues',
        uniqBy([...customGroupValues, ...customValuesToAdd], 'value')
      );
      return false;
    },
    [options, values.groupValues, values.customGroupValues, setFieldValue]
  );

  const onCollapseButtonClick = useCallback(() => {
    setShowInventory((prev) => !prev);
  }, []);

  const collapseButtonTitle = useMemo(() => {
    return showInventory
      ? i18n.t('productGroupForm.buttons.finishEdit')
      : i18n.t(`productGroupForm.buttons.edit${capitalize(groupType)}`);
  }, [showInventory, groupType]);

  return (
    <Page.Section
      title={i18n.t(
        isStoreUnit
          ? 'productGroupForm.title.selectStoreSection'
          : isDeviceUnit
            ? 'productGroupForm.title.selectDeviceSection'
            : `productGroupForm.title.select${capitalize(values.groupType)}Section`
      )}
      titleHint={
        groupType === ProductGroupType.ADSPACE
          ? i18n.t(
              getDeliveryUnitI18nKeys(isStoreUnit, isDeviceUnit).conditionHint
            )
          : undefined
      }
      inlineTitle
      withShadow={false}
      className={cx({
        [styles.sectionOnlyTitle]: !showInventory && groupValues.length === 0
      })}
    >
      <div className={styles.collapseButton} onClick={onCollapseButtonClick}>
        {collapseButtonTitle}
      </div>
      <div style={{ display: 'flex' }}>
        {showInventory && (
          <div className={styles.options}>
            <SelectComponent model={selectComponentModel} />
          </div>
        )}
        {(showInventory || groupValues.length > 0) && (
          <div
            className={styles.selectedFilters}
            onPaste={handlePasteFromClipboard}
          >
            <LimitationSet
              value={groupValues}
              type='groupValues'
              needShow
              dropdownHidden
              showPlus={false}
              showClearButton
              onChange={onGroupsChange}
              deleteLimitation={onClearGroups}
              errorTip={hasError ? <Tips>{error}</Tips> : undefined}
              disable={!showInventory}
            />
            <span
              className={styles.copyHintLabel}
              onClick={handleCopyToClipboard}
            >
              {i18n.t('common.labels.copyToClipBoard')}
            </span>
          </div>
        )}
      </div>
    </Page.Section>
  );
};
